import React, { FC } from "react";
import { DEMO_STAY_LISTINGS } from "data/listings";
import { Link } from "react-router-dom";
import BtnLikeIcon from "components/BtnLikeIcon/BtnLikeIcon";
import { currencySymbol } from "shared/CurrencySymbol";
import GallerySliderV4 from "components/GallerySlider/GallerySliderV4";
import Badge from "shared/Badge/Badge";

export interface StayCardV6Props {
  className?: string;
  listingdata?: any;
  size?: "default" | "small";
}

const DATA = DEMO_STAY_LISTINGS[0];

const StayCardV6: FC<StayCardV6Props> = ({
  size = "default",
  className = "",
  listingdata = DATA,
}) => {
  const {
    property: {
      place_images,
      title,
      price_per_month,
      property_type,
      rooms,
      city,
      state,
      property_state,
      cover_image,
      is_shared_property,
      total_bedrooms,
      total_bathrooms,
    },
  } = listingdata;

  const href = `/listing-stay-detail/${listingdata?.property?.slug}`;
  const mergedImages =
    cover_image && cover_image?.url
      ? [cover_image, ...(place_images || [])]
      : [...(place_images || [])];

  // Calculate the correct price based on property type
  const getDisplayPrice = () => {
    if (is_shared_property && rooms && rooms.length > 0) {
      // For shared properties, find the lowest room price
      const roomPrices = rooms
        .map((room: any) => parseFloat(room.price_per_month))
        .filter((price: number) => !isNaN(price) && price > 0);

      if (roomPrices.length > 0) {
        const lowestPrice = Math.min(...roomPrices);
        return {
          price: lowestPrice,
          isStartingFrom: true,
        };
      }
    }

    // For entire properties, use the property price
    if (price_per_month && parseFloat(price_per_month) > 0) {
      return {
        price: parseFloat(price_per_month),
        isStartingFrom: false,
      };
    }

    return null;
  };

  const priceInfo = getDisplayPrice();

  const modifiedStatus = (status: string | undefined) => {
    switch (status) {
      case "waiting_for_approval":
        return "Pending Review";
      case "approved":
        return "Active";
      case "rejected":
        return "Needs Updates";
      default:
        return "Draft";
    }
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case "waiting_for_approval":
        return "yellow";
      case "approved":
        return "green";
      case "rejected":
        return "red";
      default:
        return "gray";
    }
  };

  const renderSliderGallery = () => {
    return (
      <div className="relative w-full group/gallery">
        <GallerySliderV4
          uniqueID={`${listingdata?.property?.slug}`}
          ratioClass="aspect-w-4 aspect-h-3"
          galleryImgs={mergedImages}
          href={href}
        />
        
        {/* Status Badge - Top Left */}
        <div className="absolute left-3 top-3 z-[1]">
          <div
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-md shadow-lg ${
              property_state === "approved"
                ? "bg-green-500/90 text-white"
                : property_state === "waiting_for_approval"
                ? "bg-yellow-500/90 text-white"
                : property_state === "rejected"
                ? "bg-red-500/90 text-white"
                : "bg-gray-500/90 text-white"
            }`}
          >
            {property_state === "approved" && (
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {property_state === "waiting_for_approval" && (
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {modifiedStatus(property_state)}
          </div>
        </div>

        {/* Property Type Badge - Top Right */}
        {is_shared_property !== undefined && (
          <div className="absolute right-3 top-3 z-[1]">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/90 dark:bg-gray-900/90 text-gray-900 dark:text-white backdrop-blur-md shadow-lg">
              {is_shared_property ? (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  Shared
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Entire Place
                </>
              )}
            </div>
          </div>
        )}

        {/* Image Count Indicator - Bottom Right */}
        {mergedImages && mergedImages.length > 1 && (
          <div className="absolute right-3 bottom-3 z-[1]">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-black/60 text-white backdrop-blur-sm">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              {mergedImages.length}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className={size === "default" ? "p-5 space-y-4" : "p-3 space-y-2"}>
        {/* Title and Type */}
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <h2
              className={`font-semibold text-gray-900 dark:text-white capitalize ${
                size === "default" ? "text-lg" : "text-base"
              }`}
            >
              <span className="line-clamp-2">{title}</span>
            </h2>
          </div>

          {/* Property Type Badge */}
          {property_type?.name && (
            <div className="inline-flex">
              <span className="px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full">
                {property_type.name}
              </span>
            </div>
          )}
        </div>

        {/* Location */}
        <div className="flex items-center text-gray-600 dark:text-gray-400 text-sm gap-1.5">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="line-clamp-1">
            {city}, {state}
          </span>
        </div>

        {/* Room Details */}
        {(total_bedrooms || total_bathrooms || (rooms && rooms.length > 0)) && (
          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            {total_bedrooms && (
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <span>{total_bedrooms} bed{total_bedrooms !== 1 ? 's' : ''}</span>
              </div>
            )}
            {total_bathrooms && (
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
                  />
                </svg>
                <span>{total_bathrooms} bath{total_bathrooms !== 1 ? 's' : ''}</span>
              </div>
            )}
            {is_shared_property && rooms && rooms.length > 0 && (
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <span>{rooms.length} room{rooms.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-gray-200 dark:border-gray-800"></div>

        {/* Price */}
        <div className="flex justify-between items-end">
          <div>
            {priceInfo ? (
              <div className="flex flex-col">
                {priceInfo.isStartingFrom && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    Starting from
                  </span>
                )}
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    {currencySymbol()}{priceInfo.price.toFixed(0)}
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    /month
                  </span>
                </div>
              </div>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                Price not available
              </span>
            )}
          </div>

          {/* View Details Button */}
          <div className="flex items-center gap-1 text-[#F56948] text-sm font-medium group-hover:gap-2 transition-all">
            <span>Details</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`nc-StayCard group relative bg-white dark:bg-neutral-900 border-2 border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden will-change-transform hover:shadow-2xl hover:border-[#F56948] transition-all duration-300 ${className}`}
      data-nc-id="StayCard"
    >
      {renderSliderGallery()}
      <Link to={href}>{renderContent()}</Link>
    </div>
  );
};

export default StayCardV6;